/**
 * Shared Input Styles
 *
 * Common styles for Input and DecimalInput components.
 * Based on Figma XDS design system.
 *
 * Focus behavior: Border change only (no ring) - per Figma spec
 */

/**
 * Size height mapping for input components
 * Used by both Input (full styles) and DecimalInput (height only)
 */
export const inputSizeHeights = {
  xxs: 'h-6', // 24px
  xs: 'h-7', // 28px
  sm: 'h-8', // 32px
  md: 'h-9', // 36px
  lg: 'h-10', // 40px
} as const;

/**
 * Full size variants for input components (height + padding + text)
 * - Input supports all sizes: xxs, xs, sm, md, lg
 * - DecimalInput uses inputSizeHeights directly
 *
 * Horizontal padding per design spec:
 * - xxs, xs: 10px (px-2.5)
 * - sm, md: 12px (px-3)
 * - lg: 14px (px-3.5)
 */
export const inputSizeStyles = {
  xxs: 'text-body2 h-6 py-0.5 px-2.5',
  xs: 'text-body2 h-7 py-0.5 px-2.5',
  sm: 'text-body2 h-8 py-1 px-3',
  md: 'text-body2 h-9 py-1 px-3',
  lg: 'text-body1 h-10 py-1.5 px-3.5',
} as const;

export type InputSize = keyof typeof inputSizeStyles;
export type DecimalInputSize = Extract<InputSize, 'md' | 'lg'>;

/**
 * Get input size height class
 */
export function getInputSizeHeightClass(size: InputSize): string {
  return inputSizeHeights[size];
}

/**
 * Border color states for input components
 * Focus: border-ring (no ring, just border change)
 * Error: border-destructive
 * Default: border-input-border
 */
export const inputBorderStyles = {
  default: 'border-input-border',
  focus: 'border-ring',
  error: 'border-destructive',
} as const;

/**
 * Get border class based on state
 * Priority: error > focus > default
 */
export function getInputBorderClass(options: {
  error?: boolean;
  isFocused?: boolean;
}): string {
  const { error, isFocused } = options;
  if (error) return inputBorderStyles.error;
  if (isFocused) return inputBorderStyles.focus;
  return inputBorderStyles.default;
}

/**
 * Base input styles shared between Input and DecimalInput
 * Note: Horizontal padding is now defined per size in inputSizeStyles
 */
export const inputBaseStyles = [
  'bg-input dark:bg-input/30',
  'flex w-full rounded-md border transition-colors',
  'disabled:cursor-not-allowed disabled:opacity-50',
  'focus:outline-none',
] as const;

/**
 * Focus styles for input - border only (no ring per Figma)
 */
export const inputFocusStyles = {
  default: 'focus:border-ring',
  error: '', // Error state keeps destructive border on focus
} as const;
